import { Button } from 'components/Button'
import { TakaLogo } from 'components/TakaLogo'
import { reloadToRoot } from 'lib/utils'
import { AlertCircle, Loader2, MailWarning } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PRICING_DATA_THEME } from 'modules/billing/pricing-theme'
import { selectCurrentUser, selectIsLoggedIn } from 'modules/common'
import { useAppDispatch, useAppSelector } from 'modules/core'
import { switchBusinessAction } from 'modules/business'
import {
  acceptInviteByTokenAction,
  logoutAction,
  selectRequiresBusinessCreation,
} from 'modules/authentication'
import { ApiError } from 'openapi'
import { BusinessMembersService } from 'openapi/services/BusinessMembersService'
import type { InvitePreviewResponseDto } from 'openapi/models/InvitePreviewResponseDto'

const ROLE_LABELS: Record<string, string> = {
  owner: 'an owner',
  admin: 'an admin',
  member: 'a member',
  viewer: 'a viewer',
}

type Phase = 'loading' | 'ready' | 'invalid' | 'accepting' | 'wrong-email'

/**
 * The invitation screen. Two modes:
 *  - TOKEN mode (`/accept-invite/:token`, from an emailed link): previews the
 *    invite by its public token and accepts via accept-by-token. Works logged
 *    out (sends to login first) and can hit the wrong-account (403) state.
 *  - SESSION mode (`/accept-invite`, no token): a freshly-signed-in invitee who
 *    came through normal signup (not the link). We pull their pending invite
 *    from my-invitations and accept by membership id — the same full-screen
 *    treatment instead of a card bolted onto onboarding.
 */
export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const isLoggedIn = useAppSelector(selectIsLoggedIn)
  const currentUser = useAppSelector(selectCurrentUser)
  const requiresBusinessCreation = useAppSelector(selectRequiresBusinessCreation)

  const isTokenMode = Boolean(token)
  const [phase, setPhase] = useState<Phase>('loading')
  const [preview, setPreview] = useState<InvitePreviewResponseDto | null>(null)
  // SESSION mode accepts by membership id (no token in hand).
  const [membershipId, setMembershipId] = useState<string | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    setPhase('loading')

    if (token) {
      // TOKEN mode: public preview by token.
      BusinessMembersService.publicInvitesControllerGetInvitePreviewV1(token)
        .then(res => {
          if (cancelled) return
          setPreview(res)
          // Expired previews still get the Accept button: the server now
          // auto-accepts an expired token for the RIGHTFUL invitee (identity
          // beats the 7-day formality) and 410s anyone else — so the emailed
          // link a stranded invitee holds keeps working past day 7.
          setPhase('ready')
        })
        .catch(err => {
          if (cancelled) return
          // Unknown/revoked token → 404 → friendly invalid state (not
          // error-tracking-worthy). Anything else (500, network) is a real problem.
          if (!(err instanceof ApiError && err.status === 404)) {
            console.error('Invite preview failed:', err)
          }
          setPhase('invalid')
        })
    } else {
      // SESSION mode: the logged-in user's own pending invite.
      BusinessMembersService.businessMembershipsControllerFindMyInvitationsV1()
        .then(res => {
          if (cancelled) return
          const invite = res.data?.[0]
          if (!invite) {
            // Nothing to accept — don't strand them on a blank invite screen.
            navigate('/', { replace: true })
            return
          }
          setMembershipId(invite.id)
          setPreview({
            // Falsy-OR, not ??, so an empty-string name/business (owner with no
            // display name) still falls back — matches the server preview's
            // `name || email` behavior (business-memberships.service.ts).
            businessName: invite.business?.name || 'a business',
            inviterName: invite.invitedBy?.name || invite.invitedBy?.email || 'Someone',
            // Only rendered in token mode / the wrong-email state, neither of
            // which session mode can reach — no need to fill it in.
            invitedEmail: '',
            role: (invite.role ?? 'member') as InvitePreviewResponseDto['role'],
            expired: false,
          })
          setPhase('ready')
        })
        .catch(err => {
          if (cancelled) return
          // Authenticated read of the user's own invites failing is a real
          // problem — except a 401 (raw service call, no apiCall refresh
          // retry), which just means the session is mid-refresh.
          if (!(err instanceof ApiError && err.status === 401)) {
            console.error('Failed to load pending invitations:', err)
          }
          navigate('/', { replace: true })
        })
    }

    return () => {
      cancelled = true
    }
  }, [token, navigate])

  const handleAccept = useCallback(async () => {
    setError('')
    setPhase('accepting')

    if (token) {
      // TOKEN mode: accept-by-token mints a business-scoped session; the slice
      // matcher applies it, then a hard reload re-bootstraps inside the business.
      try {
        await dispatch(acceptInviteByTokenAction({ token })).unwrap()
        reloadToRoot()
      } catch (err) {
        // `.unwrap()` re-throws the thunk's rejectWithValue payload — the server
        // error body ({ statusCode, message }) — read the statusCode to
        // distinguish an email-mismatch (403).
        const payload = err as { message?: string; statusCode?: number }
        if (payload?.statusCode === 403) {
          setPhase('wrong-email')
          return
        }
        // 410 = already-used/expired invite — an expected condition (e.g.
        // accepted in another tab); the server message is shown below.
        if (payload?.statusCode !== 410) {
          console.error('Accept invite by token failed:', err)
        }
        setError(payload?.message ?? 'Something went wrong accepting this invite.')
        setPhase('ready')
      }
      return
    }

    // SESSION mode: accept by membership id, then switch into the business
    // (mirrors the pending-invitations banner).
    if (!membershipId) return
    try {
      const membership =
        await BusinessMembersService.businessMembershipsControllerAcceptInvitationV1(membershipId)
      if (membership.businessId) {
        await dispatch(switchBusinessAction({ businessId: membership.businessId })).unwrap()
      }
      reloadToRoot()
    } catch (err) {
      console.error('Failed to accept invitation:', err)
      setError('Something went wrong accepting this invite.')
      setPhase('ready')
    }
  }, [dispatch, token, membershipId])

  const handleLoginRedirect = useCallback(() => {
    // The preview returns a masked email that LoginForm's regex always rejects,
    // so prefilling it is a guaranteed no-op — just carry the redirect back here
    // so the user lands on this invite again after OTP.
    const params = new URLSearchParams({ redirectUrl: `/accept-invite/${token}` })
    navigate(`/login?${params.toString()}`)
  }, [navigate, token])

  const handleSignOutAndRetry = useCallback(async () => {
    // After logout the route tree flips to logged-out and this page remounts
    // at its logged-out tree position — the effect refetches the preview and
    // sets the phase, so nothing else to do here.
    await dispatch(logoutAction())
  }, [dispatch])

  const handleCreateOwnInstead = useCallback(() => {
    // They picked building their own business over this invite. The invite stays
    // pending server-side; the in-app pending-invitations banner offers it again
    // after they finish creating their own business.
    // A zero-business invited user (PENDING bid) is force-navigated to /onboarding
    // by OnboardingGuard; /businesses/new runs DarkOnboardingFlow in
    // additional-business mode which assumes an existing business + forces the
    // paywall, so it's wrong for them. Existing users add via /businesses/new.
    navigate(requiresBusinessCreation ? '/onboarding' : '/businesses/new')
  }, [navigate, requiresBusinessCreation])

  return (
    <div
      className="taka-dark relative flex min-h-screen w-full flex-col items-center justify-center overflow-hidden bg-bg px-6 text-paper"
      data-theme={PRICING_DATA_THEME}
    >
      <div className="grid-bg pointer-events-none absolute inset-0 opacity-60" aria-hidden />
      <a
        href="/"
        className="absolute top-6 left-6 z-20 flex items-center text-acid lg:top-8 lg:left-10"
        aria-label="Taka"
      >
        <TakaLogo className="h-7 w-auto" />
      </a>

      <div className="relative z-10 w-full max-w-[420px] rounded-[16px] border border-line bg-bg-2 p-8 text-center">
        {phase === 'loading' && (
          <div className="flex flex-col items-center gap-3 py-6">
            <Loader2 className="h-6 w-6 animate-spin text-paper-faint" />
            <p className="text-[13px] text-paper-dim">Checking your invite…</p>
          </div>
        )}

        {phase === 'invalid' && (
          <div className="flex flex-col items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full border border-line-bright bg-bg-3 text-paper-dim">
              <AlertCircle size={22} />
            </div>
            <h1 className="text-[20px] font-semibold text-paper">This invite is no longer valid</h1>
            <p className="text-[14px] text-paper-dim">Ask whoever invited you to send a new one.</p>
            <Button
              variant="t3-primary"
              size="t3-md"
              className="mt-2 w-full"
              onClick={() => navigate('/')}
            >
              Go to Taka
            </Button>
          </div>
        )}

        {phase === 'wrong-email' && (
          <div className="flex flex-col items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full border border-line-bright bg-bg-3 text-paper-dim">
              <MailWarning size={22} />
            </div>
            <h1 className="text-[20px] font-semibold text-paper">Wrong account</h1>
            <p className="text-[14px] text-paper-dim">
              This invite was sent to{' '}
              <span className="font-medium text-paper">{preview?.invitedEmail}</span>. You're signed
              in as <span className="font-medium text-paper">{currentUser?.email}</span>. Sign out
              and sign back in with the invited email to accept.
            </p>
            <Button
              variant="t3-primary"
              size="t3-md"
              className="mt-2 w-full"
              onClick={handleSignOutAndRetry}
            >
              Sign out and retry
            </Button>
            <Button
              variant="t3-tertiary"
              size="t3-md"
              className="w-full"
              onClick={() => navigate('/')}
            >
              Back to Taka
            </Button>
          </div>
        )}

        {(phase === 'ready' || phase === 'accepting') && preview && (
          <div className="flex flex-col items-center gap-5">
            <TakaLogo className="h-9 w-auto text-acid" />
            <h1 className="text-[22px] leading-snug font-semibold text-paper">
              <span className="font-semibold">{preview.inviterName}</span> invited you to join{' '}
              <span className="font-semibold">{preview.businessName}</span> as{' '}
              {ROLE_LABELS[preview.role] ?? 'a collaborator'}.
            </h1>

            {error && <p className="text-[13px] text-[color:var(--alert-error-text)]">{error}</p>}

            {isLoggedIn ? (
              <div className="flex w-full flex-col gap-2">
                <Button
                  variant="t3-primary"
                  size="t3-md"
                  className="w-full"
                  onClick={handleAccept}
                  disabled={phase === 'accepting'}
                >
                  {phase === 'accepting' ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    'Accept invitation'
                  )}
                </Button>
                <Button
                  variant="t3-tertiary"
                  size="t3-md"
                  className="w-full"
                  onClick={handleCreateOwnInstead}
                  disabled={phase === 'accepting'}
                >
                  Create my own business instead
                </Button>
                {/* A user with their own business can defer the decision — the
                    invite stays pending and the login redirect re-offers it.
                    Hidden for pending-bid users: '/' would bounce them right
                    back here (their escape is create-my-own → onboarding). */}
                {!requiresBusinessCreation && (
                  <Button
                    variant="t3-tertiary"
                    size="t3-md"
                    className="w-full"
                    onClick={() => navigate('/')}
                    disabled={phase === 'accepting'}
                  >
                    Not now
                  </Button>
                )}
              </div>
            ) : (
              <Button
                variant="t3-primary"
                size="t3-md"
                className="w-full"
                onClick={handleLoginRedirect}
              >
                Accept invitation
              </Button>
            )}

            {/* Only meaningful in token mode (masked email for a logged-out visitor);
                in session mode it's the user's own address, so we omit it. */}
            {isTokenMode && (
              <p className="text-[12px] text-paper-faint">Invited as {preview.invitedEmail}</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
